/**
 * Redakční obsah nabídkových stránek a práh indexace (vize struktura-webu C2/C3).
 *
 * JEDINÉ místo, které ví:
 *   - jak se obsah KLÍČUJE (`all` + tři typy zájezdu — `zajezdy/<destinace>` je taky
 *     nabídková stránka a bez vlastního textu by prahem nikdy neprošla),
 *   - kdy nabídková stránka PROJDE prahem (≥ 3 zájezdy A vyplněný úvodní text).
 *
 * Proč v `packages/shared`: verdikt počítá API (koncový bod obsahu i sitemapa) a web ho
 * jen čte (vize J6). Kdyby si ho počítal každý sám, sitemapa by nabídla adresu, která
 * o sobě tvrdí `noindex` — nekonzistence, na kterou vyhledávače reagují vyřazením shluku.
 * Administrace tytéž funkce volat SMÍ a má: verdikt se musí hýbat při psaní, ještě před
 * uložením (scénář „Redaktorka obsahu").
 */
import { z } from "zod";
import { TRIP_TYPES, type TripType } from "./types";
import { toPlainText } from "./utils";

/**
 * Klíče, pod kterými se obsah ukládá u destinace. `all` = nabídka destinace bez
 * zvoleného typu; zbytek doslova kopíruje `TripType`, takže nový typ zájezdu klíč
 * dostane automaticky (a typecheck to vynutí u každého `Record<OfferContentKey, …>`).
 */
export const OFFER_CONTENT_KEYS = ["all", ...TRIP_TYPES] as const;
export type OfferContentKey = (typeof OFFER_CONTENT_KEYS)[number];

/** Zvolený typ zájezdu → klíč obsahu. Žádný typ = nabídka celé destinace. */
export function offerContentKey(tripType: TripType | null | undefined): OfferContentKey {
  return tripType ?? "all";
}

export interface OfferFaqItem {
  question: string;
  answer: string;
}

/** Tvar sdílený s `destinations.faqs` — sekcový FAQ z admin editoru (`FaqEditor`). */
export interface OfferFaqSection {
  topic: string;
  items: OfferFaqItem[];
}

export interface OfferPageContent {
  /** Rich text (HTML z `RichTextEditor`) pod H1 výpisu. */
  intro?: string;
  faqs?: OfferFaqSection[];
}

export type OfferContentMap = Partial<Record<OfferContentKey, OfferPageContent>>;

/** Vize C2: „aspoň tři zájezdy". */
export const OFFER_INDEX_MIN_TRIPS = 3;

/**
 * Kolik znaků PROSTÉHO textu musí mít úvod, aby se počítal za „vyplněný".
 *
 * Není to číslo z vize, je to naše konvence — „vyplněný obsah" musí znamenat odstavec,
 * ne dvě slova. Je to jedna konstanta na jednom místě a administrace ji ukazuje jako
 * počítadlo, takže redaktorka nikdy nehádá, proč stránka není v indexu.
 */
export const OFFER_INTRO_MIN_CHARS = 80;

/**
 * Délka úvodního textu v PROSTÉM textu.
 *
 * Bez odstranění značek by prahem prošlo prázdné `<p></p>`, které `RichTextEditor` ukládá
 * jako „nic" — a práh by byl jen na papíře.
 *
 * Strip-HTML se tu NEPÍŠE ZNOVU: `toPlainText` je jeho kanonický domov (viz jeho hlavička)
 * a vlastní kopie by byla chudší přesně tam, kde na tom u prahu záleží — vyhazuje celý
 * obsah `<script>`/`<style>` (jinak by se do délky započítalo CSS, které na stránce
 * nikdo neuvidí) a dekóduje i hexadecimální entity.
 */
export function offerIntroLength(intro: string | null | undefined): number {
  return toPlainText(intro).length;
}

/**
 * Prošla kombinace (destinace × typ) prahem indexace? Konjunkce obou podmínek vize C2 —
 * počet zájezdů SÁM O SOBĚ nestačí a vyplněný text sám o sobě taky ne.
 */
export function offerPageIndexable(input: { tripCount: number; intro?: string | null }): boolean {
  return (
    input.tripCount >= OFFER_INDEX_MIN_TRIPS &&
    offerIntroLength(input.intro) >= OFFER_INTRO_MIN_CHARS
  );
}

/**
 * Sekcový FAQ (`[{ topic, items: [{ question, answer }] }]`) — JEDNA definice sdílená
 * sloupcem `destinations.faqs` i obsahem nabídkové stránky. Druhá kopie by znamenala, že
 * se ty dva tvary rozejdou první změnou editoru, který je oba vyrábí (`FaqEditor`).
 */
export const faqSectionsSchema = z.array(
  z.object({
    topic: z.string().default("General"),
    items: z.array(
      z.object({
        question: z.string(),
        answer: z.string(),
      }),
    ),
  }),
);

export const offerPageContentSchema = z.object({
  intro: z.string().optional(),
  faqs: faqSectionsSchema.optional(),
});

/**
 * Mapa klíč → obsah. Skládá se z `OFFER_CONTENT_KEYS`, aby nový typ zájezdu nemohl
 * zůstat bez validace; neznámé klíče Zod zahazuje (výchozí chování `z.object`).
 */
export const offerContentSchema = z.object(
  Object.fromEntries(OFFER_CONTENT_KEYS.map((key) => [key, offerPageContentSchema.optional()])) as {
    [K in OfferContentKey]: z.ZodOptional<typeof offerPageContentSchema>;
  },
);

/**
 * Cesta veřejného koncového bodu obsahu. Sdílený stavitel, ne literál na dvou místech:
 * mazání mezipaměti je EXACT-URL, takže ručně opsaná adresa v purge sadě by se s adresou,
 * kterou web doopravdy volá, rozešla tiše — přesně třída chyby, na které spadl řez 02.
 *
 * `encodeURIComponent` je u validovaného slugu destinace identita, ale slug se sem dostane
 * i z `?destination=` (parametr žádnou znakovou validací neprochází), a `#` by jinak
 * fragmentem uřízl `/offer` a stáhl by se DETAIL destinace.
 */
export function offerContentApiPath(destinationSlug: string): string {
  return `/api/destinations/${encodeURIComponent(destinationSlug)}/offer`;
}

/** Jeden klíč tak, jak ho vrací koncový bod obsahu — obsah + odvozený stav. */
export interface OfferPageState extends OfferPageContent {
  /** Počet zájezdů kombinace z kanonického roll-upu (`rollUpTripCounts`). */
  tripCount: number;
  /** Verdikt `offerPageIndexable` — web ho jen čte (J6). */
  indexable: boolean;
}

export type OfferPagesResponse = Record<OfferContentKey, OfferPageState>;
